import Keycloak from 'keycloak-js'
import { appConfig } from '@config'

type AuthMode = 'standalone' | 'iframe'

class KeycloakAuthService {
  private _kc: Keycloak
  private logPrefix = '[AuthService]'

  constructor() {
    const config = appConfig.keycloak

    this._kc = new Keycloak(config)

    // События Keycloak
    this._kc.onAuthError = (errorData: any) => {
      console.error(`${this.logPrefix} Authentication error:`, errorData)
    }

    this._kc.onAuthRefreshSuccess = () => {
      console.log(`${this.logPrefix} [onAuthRefreshSuccess] Token refreshed successfully`)
    }

    this._kc.onAuthRefreshError = () => {
      console.error(`${this.logPrefix} [onAuthRefreshError] Failed to refresh token`)
    }

    this._kc.onTokenExpired = () => {
      console.warn(`${this.logPrefix} [onTokenExpired] Token expired, attempting refresh...`)
      this.updateToken(() => {
        console.log(`${this.logPrefix} [onTokenExpired] Successfully refreshed token`)
      })
    }
  }

  /**
   * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
   *
   * @param onAuthenticatedCallback - Callback function to be called after initialization
   * @param mode - Authentication mode: 'standalone' (default) or 'iframe' for SSO
   */
  init = (onAuthenticatedCallback: () => void, mode: AuthMode = 'standalone'): void => {
    // Standalone режим - стандартная инициализация
    // checkLoginIframe: false — отключаем iframe проверку сессии из-за блокировки third-party cookies
    // в браузерах (Chrome 145+). Иначе login-status-iframe возвращает 204/ERR_ABORTED → clearToken → redirect loop
    this._kc
      .init({
        onLoad: 'login-required' as const,
        checkLoginIframe: false,
      })
      .then((authenticated: boolean) => {
        if (!authenticated) {
          console.warn(`${this.logPrefix} User is not authenticated`)
        }
        onAuthenticatedCallback()
      })
      .catch((error: any) => {
        console.error(`${this.logPrefix} Initialization error:`, error)
      })
  }

  /**
   * КРИТИЧЕСКИ ВАЖНЫЙ МЕТОД: Правильная установка токена от родителя
   * Устанавливает токен и все связанные поля в Keycloak instance
   */
  private setTokenFromParent(token: string, refreshToken?: string, tokenParsed?: any): void {
    // 1. Устанавливаем основной токен
    if (!token) {
      throw new Error('Token is required')
    }
    this._kc.token = token

    // 2. Устанавливаем refresh token если есть
    if (refreshToken) {
      this._kc.refreshToken = refreshToken
    }

    // 3. Устанавливаем распарсенный токен если есть
    if (tokenParsed) {
      this._kc.tokenParsed = tokenParsed

      // Устанавливаем subject (user ID) из токена
      if (tokenParsed.sub) {
        this._kc.subject = tokenParsed.sub
      }
    } else {
      // Если tokenParsed не передан, парсим токен вручную
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        this._kc.tokenParsed = JSON.parse(jsonPayload)

        if (this._kc.tokenParsed?.sub) {
          this._kc.subject = this._kc.tokenParsed.sub
        }
      } catch (error) {
        console.error(`${this.logPrefix} Failed to parse token:`, error)
      }
    }

    // 4. Проверяем что токен не истек
    if (this._kc.isTokenExpired()) {
      console.warn(`${this.logPrefix} WARNING: Token is already expired!`)
    }

    // 5. Устанавливаем authenticated флаг
    this._kc.authenticated = true
  }

  doLogin = () => this._kc.login()

  doLogout = () => this._kc.logout()

  getToken = () => this._kc.token

  isLoggedIn = () => {
    const loggedIn = !!this._kc.token
    // Логируем только если не залогинен (проблема)
    if (!loggedIn) {
      console.warn(`${this.logPrefix} [isLoggedIn] User is not logged in`, {
        token: this._kc.token ? 'present' : 'missing',
        tokenExpired: this._kc.isTokenExpired(),
      })
    }

    return loggedIn
  }

  updateToken = (successCallback: () => void) => {
    console.log(`${this.logPrefix} [updateToken] Attempting to update token...`, {
      currentToken: this._kc.token ? 'present' : 'missing',
    })

    // Standalone режим - стандартное обновление через Keycloak
    return this._kc
      .updateToken(5)
      .then(() => {
        console.log(`${this.logPrefix} [updateToken] Token updated successfully`, {
          token: this._kc.token ? 'present' : 'missing',
          tokenExpired: this._kc.isTokenExpired(),
        })
        successCallback()
      })
      .catch((error: any) => {
        console.error(`${this.logPrefix} [updateToken] Failed to update token:`, error)
        console.log(`${this.logPrefix} [updateToken] Redirecting to login...`)
        this.doLogin()
      })
  }

  getUsername = (): string => {
    const username = this._kc.tokenParsed?.preferred_username
    // Логируем только если username недоступен (проблема)
    if (!username) {
      console.warn(`${this.logPrefix} [getUsername] Username not available`, {
        tokenParsed: this._kc.tokenParsed ? 'present' : 'missing',
        token: this._kc.token ? 'present' : 'missing',
      })
    }

    return username || ''
  }

  hasRole = (roles: string[]) => {
    if (appConfig.roleSource === 'client') {
      const resource = appConfig.roleSourceClient || this._kc.clientId

      return roles.some(role => this._kc.hasResourceRole(role, resource))
    }

    return roles.some(role => this._kc.hasRealmRole(role))
  }

  setToken = (token: string) => {
    console.log(`${this.logPrefix} [setToken] Setting token`, {
      tokenLength: token?.length || 0,
      tokenPresent: !!token,
    })
    this._kc.token = token
  }

  isTokenExpired = () => {
    const expired = this._kc.isTokenExpired()
    if (expired) {
      console.warn(`${this.logPrefix} [isTokenExpired] Token is expired!`)
    }

    return expired
  }
}

const AuthService = new KeycloakAuthService()

export default AuthService
